'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@ahafood/ui/components/drawer';
import { Image } from '@ahafood/ui/components/image';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemHeader,
} from '@ahafood/ui/components/item';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@ahafood/ui/components/sheet';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { getCookie } from 'cookies-next';
import {
  MoreHorizontal,
  Settings2Icon,
  Star,
  X,
  Edit3,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/styles.css';
import { toast } from 'sonner';
import AiSummary from '../ai-summary';
import { LightboxPortal } from './lightbox';
import { RatingDistribution } from './rating-distribution';
import type { RatingFilters } from './rating-sheet-context';
import type { Store } from '@/api/ahafood/get-store-fn';
import { useDeleteRating } from '@/api/use-delete-rating-image';
import {
  getStoreRatingFn,
  type MyRating,
  type OtherRating,
} from '@/api/use-get-rating';
import { DateFormat, formatUnixTime } from '@/lib/datetime';
import { useIsMobile } from '@/lib/is-mobile';
import { useRating } from '@/providers/rating-provider';
import { useAuthStore } from '@/store';

const PAGE_LIMIT = 20;

// Custom dropdown menu that works better in sheets
function RatingDropdownMenu({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleEdit = () => {
    onEdit();
    setIsOpen(false);
  };

  const handleDelete = () => {
    onDelete();
    setIsOpen(false);
  };

  return (
    <div className="relative z-[51]">
      <Button
        ref={buttonRef}
        variant="ghost"
        size="sm"
        className="size-6 p-0 [&_svg]:size-5 [&_svg]:text-neutral-60 [&_svg]:font-bold"
        onClick={() => setIsOpen(!isOpen)}
      >
        <MoreHorizontal />
      </Button>

      {isOpen && (
        <div
          ref={menuRef}
          className="absolute right-0 top-full mt-1 w-32 bg-white border border-neutral-20 rounded-lg shadow-lg py-1"
        >
          <button
            onClick={handleEdit}
            className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-10 flex items-center gap-2"
          >
            <Edit3 className="size-4" />
            Chỉnh sửa
          </button>
          <button
            onClick={handleDelete}
            className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-10 flex items-center gap-2 text-red-60"
          >
            <Trash2 className="size-4" />
            Xóa
          </button>
        </div>
      )}
    </div>
  );
}

function useLockBodyScroll(locked: boolean) {
  useEffect(() => {
    if (locked) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [locked]);
}

export function RatingSheet({
  store,
  initialRatings,
  filters,
  onClose,
  onFiltersChange,
}: {
  store: Store | null;
  initialRatings: (MyRating | OtherRating)[];
  filters: RatingFilters;
  onClose: () => void;
  onFiltersChange: (filters: RatingFilters) => void;
}) {
  const token = String(getCookie('ws_token') ?? '');
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useLockBodyScroll(open);

  useEffect(() => setOpen(!!store), [store]);

  const handleOpenChange = useCallback(
    (value: boolean) => {
      setOpen(value);
      if (!value) onClose();
    },
    [onClose]
  );

  // 🧠 Infinite query for store ratings - optimized with proper query key
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    refetch,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['store-ratings', store?._id, filters],
    queryFn: async ({ pageParam = 0 }) => {
      if (!store) return { my_ratings: [], other_ratings: [] };
      return getStoreRatingFn(token, {
        store_id: store._id,
        skip: pageParam,
        limit: PAGE_LIMIT,
        ...filters,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce(
        (sum, page) =>
          sum +
          (page.my_ratings?.length || 0) +
          (page.other_ratings?.length || 0),
        0
      );

      const lastPageItemCount =
        (lastPage.my_ratings?.length || 0) +
        (lastPage.other_ratings?.length || 0);

      // Nếu trang cuối có đủ PAGE_LIMIT items thì còn data
      if (lastPageItemCount < PAGE_LIMIT) {
        return undefined; // Không còn data
      }

      return totalLoaded; // Còn data, skip = tổng số items đã load
    },
    enabled: !!store && open,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // 🧩 Merge all pages
  const allRatings = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((p) => [...p.my_ratings, ...p.other_ratings]);
  }, [data]);

  // 🧹 Refetch when filters change
  useEffect(() => {
    if (open) void refetch();
  }, [filters, open, refetch]);

  const handleFilterChange = useCallback(
    (newFilters: RatingFilters) => onFiltersChange(newFilters),
    [onFiltersChange]
  );

  const handleLoadMore = useCallback(() => {
    void fetchNextPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    allRatings.length,
    data?.pages,
  ]);

  const handleRatingDeleted = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['store-ratings'],
    });
  }, [queryClient]);

  const contentProps = {
    store: store!,
    ratings: allRatings,
    filters,
    showFilters,
    onShowFiltersChange: setShowFilters,
    onFilterChange: handleFilterChange,
    onLoadMore: handleLoadMore,
    handleOpenChange: handleOpenChange,
    hasMore: !!hasNextPage,
    isLoading: isLoading || isFetchingNextPage,
    onRatingDeleted: handleRatingDeleted,
  };

  if (!store) return null;

  return !isMobile ? (
    <Dialog open={open} onOpenChange={handleOpenChange} modal={false}>
      <DialogContent className="max-w-xl max-h-[90vh] p-0 pb-8 flex flex-col">
        <DialogHeader className="p-4 shrink-0">
          <DialogTitle>Đánh giá và nhận xét</DialogTitle>
          <DialogDescription>
            Xem và quản lý tất cả các đánh giá của cửa hàng tại đây.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-hidden">
          <RatingSheetContent {...contentProps} />
        </div>
      </DialogContent>
    </Dialog>
  ) : (
    <Sheet open={open} onOpenChange={handleOpenChange} modal={false}>
      <SheetContent
        side="bottom"
        className="h-full p-0 flex flex-col"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <SheetHeader className="flex flex-row items-center shrink-0">
          <Button
            variant="ghost"
            className="focus:outline-none focus:ring-0"
            onClick={() => handleOpenChange(false)}
          >
            <X />
          </Button>
          <SheetTitle>Đánh giá và nhận xét</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-hidden">
          <RatingSheetContent {...contentProps} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Rating Item Component
function RatingItem({
  rating,
  isLast = false,
  onRatingDeleted,
}: {
  rating: MyRating | OtherRating;
  isLast?: boolean;
  onRatingDeleted?: () => void;
}) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const { user } = useAuthStore();
  const [isCommentExpanded, setIsCommentExpanded] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const token = String(getCookie('ws_token') ?? '');
  const { openCommentModal } = useRating();
  const deleteRatingMutation = useDeleteRating(token);
  const userId = user?.cunstomerId;

  // Check if this is a MyRating (has customer_id property that matches current user)
  const isMyRating = 'customer_id' in rating && rating.customer_id === userId;

  // Check if rating was created within the last 7 days
  const isWithin7Days = useMemo(() => {
    if (!isMyRating) return false;
    const now = Date.now() / 1000; // Convert to seconds
    return now - rating.create_at <= 7 * 24 * 60 * 60;
  }, [isMyRating, rating.create_at]);

  const lightboxImages = useMemo(
    () =>
      rating.image_url?.map((imageUrl, index) => ({
        src: imageUrl,
        title: `Đánh giá ${index + 1}`,
      })) ?? [],
    [rating.image_url]
  );

  const handleOpenPreview = (currentIndex: number) => {
    setLightboxIndex(currentIndex);
    setIsLightboxOpen(true);
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const today = new Date();

    // So sánh phần yyyy-mm-dd
    const isToday = date.toDateString() === today.toDateString();

    return formatUnixTime(
      timestamp,
      isToday ? DateFormat.TIME_24 : DateFormat.DATE
    );
  };

  const handleClosePreview = () => {
    setIsLightboxOpen(false);
  };

  const handleEditRating = () => {
    if (isMyRating && 'order_code' in rating) {
      const myRating = rating as MyRating;
      openCommentModal(
        {
          code: myRating.order_code,
          store_name: '', // We don't have store name in rating, but modal can work without it
        },
        myRating.overall,
        {
          comment: myRating.comment || '',
          reasons: myRating.tags || [],
          images: myRating.image_url || [],
        }
      );
    }
  };

  const handleDeleteRating = async () => {
    if (!isMyRating || !('order_code' in rating)) return;

    const myRating = rating as MyRating;

    try {
      await deleteRatingMutation.mutateAsync({
        order_code: myRating.order_code,
      });

      toast.success('Đã xóa nhận xét thành công!', {
        // action: (
        //   <button
        //     onClick={handleEditRating}
        //     className="text-primary-50 font-bold px-3 py-1 rounded-lg hover:bg-primary-5 transition-colors"
        //   >
        //     Đánh giá lại
        //   </button>
        // ),
      });

      onRatingDeleted?.();
    } catch (error) {
      toast.error('Có lỗi xảy ra khi xóa nhận xét');
    }
    setShowDeleteDialog(false);
  };

  const isCommentLong = rating.comment && rating.comment.length > 140;
  const displayComment =
    isCommentExpanded || !isCommentLong
      ? rating.comment
      : rating.comment?.substring(0, 140) + '...';

  return (
    <Item variant="default" className={cn('p-0 w-full mb-4')}>
      <ItemHeader>
        <div className="flex items-start gap-3 w-full relative">
          {isMyRating && isWithin7Days && (
            <div className="absolute right-0 top-0">
              <RatingDropdownMenu
                onEdit={handleEditRating}
                onDelete={() => setShowDeleteDialog(true)}
              />
            </div>
          )}
          <Avatar className="size-10">
            <AvatarImage
              src={'/static/images/user.webp'}
              alt="user"
              className="object-top object-cover"
            />
            <AvatarFallback>
              {rating.customer_name?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 flex flex-col gap-3 w-full">
            <div className="flex flex-col gap-1 w-full">
              <div className="font-medium">
                {rating.customer_name || 'Người dùng'}
              </div>
              <div className="w-full flex justify-between">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={cn(
                        'size-4',
                        star <= rating.overall
                          ? 'fill-yellow-40 text-yellow-40'
                          : 'text-neutral-30 fill-neutral-30'
                      )}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-muted-foreground">
                    {formatTime(rating.create_at)}
                  </div>
                </div>
              </div>
            </div>
            {rating.comment && (
              <div className="space-y-1">
                <div className="text-sm text-neutral-80 leading-relaxed">
                  {displayComment}
                </div>
                <div className="w-full flex break-words justify-end">
                  {isCommentLong && !isCommentExpanded && (
                    <button
                      onClick={() => setIsCommentExpanded(true)}
                      className="text-neutral-70 text-sm font-medium underline"
                    >
                      Xem thêm
                    </button>
                  )}
                  {isCommentLong && isCommentExpanded && (
                    <button
                      onClick={() => setIsCommentExpanded(false)}
                      className="text-neutral-70 text-sm font-medium underline"
                    >
                      Thu gọn
                    </button>
                  )}
                </div>
              </div>
            )}

            {rating.image_url && rating.image_url.length > 0 && (
              <div className="flex gap-2 overflow-x-auto max-w-[calc(100vw-40px-36px-16px)] scrollbar-hide">
                {rating.image_url.map((imageUrl, index) => (
                  <button
                    key={index}
                    className="flex-none size-20 rounded-lg overflow-hidden bg-muted cursor-pointer"
                    onClick={() => handleOpenPreview(index)}
                  >
                    <img
                      src={imageUrl}
                      alt={`Đánh giá ${index + 1}`}
                      className="size-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </ItemHeader>

      <ItemContent className={cn('gap-3', !isLast && 'border-b-2 pb-4')}>
        <div className="flex flex-row gap-3 w-full">
          {rating.display_tags && rating.display_tags.length > 0 && (
            <>
              <span className="flex-none text-neutral-60 min-w-fit">
                Đánh giá:
              </span>
              <ItemActions>
                <div className="flex flex-wrap gap-1">
                  {rating.display_tags.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="text-xs rounded-md font-medium"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </ItemActions>
            </>
          )}
        </div>

        <Badge
          variant="secondary"
          className="bg-neutral-10 p-3 text-xs font-normal rounded-lg w-full"
        >
          <span className="text-neutral-50 mr-2">Đã đặt:</span>{' '}
          {rating.item_info && rating.item_info.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {rating.item_info.map((value, index) => (
                <span className="text-primary-40 font-medium" key={index}>
                  {value.name}
                  {index < rating.item_info.length - 1 && <span>, </span>}
                </span>
              ))}
            </div>
          )}
        </Badge>
      </ItemContent>

      <LightboxPortal
        open={isLightboxOpen}
        index={lightboxIndex}
        slides={lightboxImages}
        onClose={handleClosePreview}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-sm border-none rounded-2xl">
          <DialogHeader>
            <DialogTitle>Bạn có chắc chắn xoá đánh giá?</DialogTitle>
            <DialogDescription>
              Đánh giá sẽ bị xóa vĩnh viễn và không thể khôi phục.
            </DialogDescription>
          </DialogHeader>
          <div className="flex md:flex-row flex-col justify-end gap-2 mt-4">
            <Button
              variant="default"
              className="font-bold h-10"
              onClick={handleDeleteRating}
              disabled={deleteRatingMutation.isPending}
            >
              {deleteRatingMutation.isPending ? 'Đang xóa...' : 'Xác nhận xoá'}
            </Button>
            <Button
              variant="ghost"
              className="font-bold h-10"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleteRatingMutation.isPending}
            >
              Quay lại
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Item>
  );
}

function RatingFiltersDrawer({
  filters,
  onFiltersChange,
  onClose,
}: {
  filters: RatingFilters;
  onFiltersChange: (filters: RatingFilters) => void;
  onClose: () => void;
}) {
  const [tempFilters, setTempFilters] = useState<RatingFilters>(() => ({
    overall: filters.overall,
  }));

  // Update tempFilters when filters change (only star filter)
  useEffect(() => {
    setTempFilters({ overall: filters.overall });
  }, [filters.overall]);

  const handleStarFilter = (star: number) => {
    const newFilters = { ...tempFilters };
    if (newFilters.overall === star) {
      delete newFilters.overall;
    } else {
      newFilters.overall = star;
    }
    setTempFilters(newFilters);
  };

  const applyFilters = () => {
    // Start with existing text/image filters
    const newFilters: RatingFilters = {
      required_text: filters.required_text,
      required_image: filters.required_image,
    };

    // Add star filter only if it exists in tempFilters
    if (tempFilters.overall !== undefined) {
      newFilters.overall = tempFilters.overall;
    }

    // Remove undefined values
    Object.keys(newFilters).forEach((key) => {
      if (newFilters[key as keyof RatingFilters] === undefined) {
        delete newFilters[key as keyof RatingFilters];
      }
    });
    onFiltersChange(newFilters);
    onClose();
  };

  const resetFilters = () => {
    const resetStarFilters = {};
    setTempFilters(resetStarFilters);
    // Áp dụng ngay lập tức để xóa filter
    const newFilters = {
      required_text: filters.required_text,
      required_image: filters.required_image,
    };
    onFiltersChange(newFilters);
    onClose();
  };

  const activeFilterCount = Object.keys(tempFilters).filter(
    (key) =>
      tempFilters[key as keyof RatingFilters] !== undefined &&
      tempFilters[key as keyof RatingFilters] !== false
  ).length;

  return (
    <div className="px-4 pb-6 space-y-6">
      {/* Star Filter */}
      <div>
        <h3 className="font-medium mb-3 text-base">Loại đánh giá</h3>
        <div className="flex gap-2 flex-wrap">
          {[5, 4, 3, 2, 1].map((star) => (
            <Button
              key={star}
              variant="outline"
              size="sm"
              onClick={() => handleStarFilter(star)}
              className={cn(
                'flex items-center gap-1 px-3 py-1.5 bg-white flex-1 hover:bg-white',
                tempFilters.overall === star
                  ? 'text-primary-40 border-primary-40 hover:text-primary-40'
                  : 'border-neutral-20 text-neutral-70'
              )}
            >
              <span>{star} sao</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 pt-4">
        <Button
          variant="secondary"
          onClick={resetFilters}
          className="flex-1 h-14 bg-red-10 text-red-50 font-bold"
        >
          Đặt lại
        </Button>
        <Button
          variant="default"
          onClick={applyFilters}
          className="flex-1 h-14 bg-primary-40 font-bold"
        >
          Áp dụng {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
        </Button>
      </div>
    </div>
  );
}

function RatingSheetContent({
  store,
  ratings,
  filters,
  showFilters,
  onShowFiltersChange,
  onFilterChange,
  onLoadMore,
  hasMore,
  isLoading,
  handleOpenChange,
  onRatingDeleted,
}: any) {
  if (store?.rating.total === 0) {
    return (
      <div className="text-center flex flex-col px-6 py-8 text-muted-foreground items-center h-full overflow-auto">
        <div
          style={{
            background:
              'linear-gradient(to right, #4285F4, #34A853, #FBBC05, #EA4335)',
          }}
          className="rounded-2xl p-px flex items-center gap-4 w-full"
        >
          <div className="size-full bg-white rounded-2xl py-4 flex flex-col items-center gap-2">
            <div className="mb-4 flex items-center gap-2">
              <img
                src="/static/images/gg-rating.webp"
                alt="Store"
                className="inline-block mr-1 h-12 w-auto object-contain mb-1"
              />
              <span className="text-5xl font-semibold align-bottom text-neutral-90">
                {store.rating.google_average.toFixed(1)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={cn(
                    'size-4 shrink-0',
                    star <= store.rating.google_average
                      ? 'fill-yellow-40 text-yellow-40'
                      : 'fill-neutral-30 text-neutral-30'
                  )}
                />
              ))}
            </div>
            <span className="text-sm text-neutral-90 ml-2">
              {store.rating.google_total || 0} đánh giá
            </span>
          </div>
        </div>
        <Image
          src={`${assetPrefix}/static/images/robot-rating.webp`}
          alt="No ratings"
          style={{ objectFit: 'contain' }}
          width={143}
          height={214}
        />
        <h1 className="text-2xl font-bold text-neutral-90">
          Chưa có đánh giá nào.
        </h1>
        <span className="text-xs mt-2 text-neutral-60">
          Hãy đặt đơn và để lại đánh giá đầu tiên nhé!
        </span>
        <Button
          onClick={() => handleOpenChange(false)}
          variant="default"
          className="bg-gradient-to-r mt-6 from-[#FFA339] to-[#FF7F32] font-bold"
        >
          Quay lại menu món
        </Button>
      </div>
    );
  }

  return (
    <div
      id="scrollableRatings"
      className={cn(
        'h-full overflow-auto px-4 pb-[env(safe-area-inset-bottom)]'
      )}
    >
      <InfiniteScroll
        dataLength={ratings.length}
        next={onLoadMore}
        hasMore={hasMore}
        loader={<LoadingSkeleton />}
        scrollThreshold={0.9}
        scrollableTarget="scrollableRatings"
      >
        {/* Rating summary - Now scrollable */}
        <div className="p-4 border rounded-2xl mb-4 border-primary-40">
          <RatingDistribution className="mb-0" store={store} />
        </div>

        <AiSummary summary={store.summary_review} />
        {/* Filters row - Now scrollable */}
        <div className="flex gap-2 my-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onShowFiltersChange(true)}
            className={cn(
              'bg-neutral-20 text-white fill-white',
              filters.overall && 'bg-primary-40 text-white fill-white'
            )}
          >
            <Settings2Icon />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onFilterChange({
                ...filters,
                required_text: !filters.required_text,
              })
            }
            className={cn(
              'flex-1',
              filters.required_text
                ? 'text-primary-40 border-primary-40'
                : 'border-neutral-20 text-neutral-70'
            )}
          >
            Kèm bình luận
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onFilterChange({
                ...filters,
                required_image: !filters.required_image,
              })
            }
            className={cn(
              'flex-1',
              filters.required_image
                ? 'text-primary-40 border-primary-40'
                : 'border-neutral-20 text-neutral-70'
            )}
          >
            Có hình ảnh
          </Button>
        </div>

        {isLoading && !ratings.length ? (
          <LoadingSkeleton />
        ) : ratings.length ? (
          ratings.map((rating, index) => (
            <RatingItem
              key={`${rating.order_code}-${index}`}
              rating={rating}
              isLast={index === ratings.length - 1}
              onRatingDeleted={onRatingDeleted}
            />
          ))
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Chưa có đánh giá nào.
          </div>
        )}
      </InfiniteScroll>

      {/* Filter Overlay */}
      {showFilters && (
        <div
          className="fixed inset-0 bg-black/50 z-[999]"
          role="button"
          tabIndex={0}
          aria-label="Đóng bộ lọc đánh giá"
          onClick={() => onShowFiltersChange(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onShowFiltersChange(false);
            }
          }}
        />
      )}

      {/* Filter Drawer */}
      <Drawer open={showFilters} onOpenChange={onShowFiltersChange}>
        <DrawerContent
          className={cn(
            'border-none rounded-t-2xl shadow-8 pb-[env(safe-area-inset-bottom)]'
          )}
        >
          <DrawerHeader>
            <DrawerTitle>Lọc đánh giá</DrawerTitle>
          </DrawerHeader>
          <RatingFiltersDrawer
            filters={filters}
            onFiltersChange={onFilterChange}
            onClose={() => onShowFiltersChange(false)}
          />
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="p-4 border rounded-lg animate-pulse">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      ))}
    </div>
  );
}
